/**
 * JSON Path Extraction Utility
 * Extracts property paths from JSON objects for filtering
 */

/**
 * Extract all JSON paths from an object recursively
 * @param {Object} obj - Object to extract paths from
 * @param {string} prefix - Current path prefix
 * @param {Set} paths - Accumulated paths
 * @param {number} maxDepth - Maximum recursion depth
 * @param {number} currentDepth - Current recursion depth
 */
function extractPathsRecursive(
  obj,
  prefix = '',
  paths = new Set(),
  maxDepth = 5,
  currentDepth = 0
) {
  if (currentDepth >= maxDepth || obj === null || typeof obj !== 'object') {
    return;
  }

  Object.keys(obj).forEach(key => {
    const path = prefix ? `${prefix}.${key}` : key;
    paths.add(path);

    if (
      typeof obj[key] === 'object' &&
      obj[key] !== null &&
      !Array.isArray(obj[key])
    ) {
      extractPathsRecursive(obj[key], path, paths, maxDepth, currentDepth + 1);
    }
  });
}

/**
 * Extract JSON paths from an array of objects
 * @param {Array} data - Array of objects
 * @param {number} sampleSize - Number of items to sample (default: 100)
 * @returns {Array<Object>} Array of {path, breadcrumb} objects
 */
export function extractJsonPaths(data, sampleSize = 100) {
  const paths = new Set();
  const sample = data.slice(0, Math.min(sampleSize, data.length));

  sample.forEach(item => {
    extractPathsRecursive(item, '', paths);
  });

  return Array.from(paths)
    .sort()
    .map(path => ({
      path,
      breadcrumb: path.replace(/\./g, ' › ')
    }));
}

/**
 * Continue extracting paths from remaining items (lazy loading)
 * @param {Array} data - Full data array
 * @param {number} startIndex - Where to start processing
 * @param {Set} existingPaths - Existing paths to add to
 * @param {Function} callback - Callback when new paths found
 * @param {number} chunkSize - Items to process per chunk
 */
export function extractJsonPathsLazy(
  data,
  startIndex,
  existingPaths,
  callback,
  chunkSize = 100
) {
  if (startIndex >= data.length) {
    return;
  }

  const processChunk = index => {
    const end = Math.min(index + chunkSize, data.length);
    const chunk = data.slice(index, end);
    const newPaths = new Set(existingPaths);

    chunk.forEach(item => {
      extractPathsRecursive(item, '', newPaths);
    });

    const addedPaths = Array.from(newPaths).filter(p => !existingPaths.has(p));

    if (addedPaths.length > 0) {
      addedPaths.forEach(p => existingPaths.add(p));
      callback(
        addedPaths.map(path => ({
          path,
          breadcrumb: path.replace(/\./g, ' › ')
        }))
      );
    }

    if (end < data.length) {
      requestIdleCallback(() => processChunk(end));
    }
  };

  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => processChunk(startIndex));
  } else {
    setTimeout(() => processChunk(startIndex), 0);
  }
}

/**
 * Get value from object using dot notation path
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot notation path (e.g., 'data.pokemonSettings.pokemonId')
 * @returns {any} Value at path or undefined
 */
export function getValueByPath(obj, path) {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Truncate text in the middle
 * @param {string} text - Text to truncate
 * @param {number} matchIndex - Index of match for context
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export function truncateMiddle(text, matchIndex = 0, maxLength = 100) {
  if (text.length <= maxLength) {
    return text;
  }

  const halfLength = Math.floor(maxLength / 2);
  const start = Math.max(0, matchIndex - halfLength);
  const end = Math.min(text.length, matchIndex + halfLength);

  let result = '';

  if (start > 0) {
    result += '...';
  }

  result += text.substring(start, end);

  if (end < text.length) {
    result += '...';
  }

  return result;
}
